/**
 * `fg.lastAuth` marker (E1 §2.3).
 *
 * Records whether the LAST resolved `/api/customer/context` was a signed-in
 * customer or an anon session. Persisted in localStorage so it survives a tab
 * close: a customer who signs out (their Shopify session lapses / they log out
 * on the storefront) returns to the SPA as a fresh anon principal, and without
 * this marker they would look identical to a brand-new visitor and land on the
 * first-run empty/door states. With it, the app can instead show a "You're
 * signed out — sign in to see your plans" state (`<SignedOutBanner>`).
 *
 * Not a credential — just a coarse `"customer" | "anon"` enum — so it carries no
 * auth exposure. Written on EVERY `applyResponse`; read (and compared to the new
 * value) at the same call to decide the "just signed out" transition.
 */

const STORAGE_KEY = "fg.lastAuth";

export type LastAuthKind = "customer" | "anon";

/** Read the previously-stored auth kind, or `null` when unset / unavailable. */
export function readLastAuth(): LastAuthKind | null {
  if (typeof window === "undefined") return null;
  try {
    const v = localStorage.getItem(STORAGE_KEY);
    return v === "customer" || v === "anon" ? v : null;
  } catch {
    return null;
  }
}

/** Persist the current auth kind. No-op when storage is unavailable. */
export function writeLastAuth(kind: LastAuthKind): void {
  if (typeof window === "undefined") return;
  try {
    localStorage.setItem(STORAGE_KEY, kind);
  } catch {
    /* ignore — private mode / blocked storage */
  }
}

/**
 * Durable "signed out" flag. Set once a customer→anon transition is detected and
 * PERSISTS across reloads (unlike the one-shot `lastAuth` comparison, which is
 * overwritten to "anon" immediately). Cleared on the next sign-in, or when the
 * stronger promoted-session hint takes over. Without this, a signed-out customer
 * looks like a fresh visitor on the SECOND reload.
 *
 * F-01-5: bounded by a 14-day TTL (matching `PROMOTED_HINT_TTL_MS`). Without it,
 * a device whose cookies were cleared but localStorage retained (browser cookie
 * eviction, "clear cookies" without site data, a shared profile) would brand a
 * genuine visitor "signed out" forever and suppress the first-run doors. The TTL
 * is anchored to the FIRST sign-out (`at` is preserved across re-writes), not the
 * last visit — otherwise a returning user would refresh the clock indefinitely
 * and the TTL would never bite. The legacy `"1"` value (no timestamp) is treated
 * as expired so old permanent latches self-heal; the next context resolve re-writes
 * it with a timestamp if the customer is still signed out.
 */
const SIGNED_OUT_KEY = "fg.signedOut";

/** 14 days — mirrors PROMOTED_HINT_TTL_MS. */
export const SIGNED_OUT_TTL_MS = 14 * 24 * 60 * 60 * 1000;

/** Return the stored first-sign-out timestamp if present and unexpired, else null. */
function readSignedOutAt(): number | null {
  if (typeof window === "undefined") return null;
  try {
    const raw = localStorage.getItem(SIGNED_OUT_KEY);
    if (!raw) return null;
    const at = (JSON.parse(raw) as { at?: unknown }).at;
    if (typeof at !== "number" || !Number.isFinite(at)) return null; // legacy "1" / malformed
    return Date.now() - at < SIGNED_OUT_TTL_MS ? at : null;
  } catch {
    return null;
  }
}

export function readSignedOut(): boolean {
  const at = readSignedOutAt();
  if (at === null) {
    // Proactively clear an expired/legacy latch so it can't linger.
    if (typeof window !== "undefined") {
      try {
        if (localStorage.getItem(SIGNED_OUT_KEY)) localStorage.removeItem(SIGNED_OUT_KEY);
      } catch {
        /* ignore */
      }
    }
    return false;
  }
  return true;
}

export function writeSignedOut(value: boolean): void {
  if (typeof window === "undefined") return;
  try {
    if (!value) {
      localStorage.removeItem(SIGNED_OUT_KEY);
      return;
    }
    // Preserve the original sign-out time so the TTL measures time-since-sign-out,
    // not time-since-last-visit.
    const existing = readSignedOutAt();
    localStorage.setItem(
      SIGNED_OUT_KEY,
      JSON.stringify({ at: existing ?? Date.now() })
    );
  } catch {
    /* ignore — private mode / blocked storage */
  }
}
